import _ from 'lodash';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { AdminUser } from '../types/admin/admin-user';
import { AdminFacility } from '../types/admin/admin-facility';
import { Organization } from '../types/organization';
import { SessionService } from './session.service';
import { AdminPreviewError } from '../types/admin/admin-preview-error';
const PNF = require('google-libphonenumber').PhoneNumberFormat;
const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();

@Injectable({
  providedIn: 'root'
})
export class UserValidationService {
  constructor(private log: LogService, private session: SessionService) {}

  validateAll(
    org: Organization,
    fac: AdminFacility,
    users: AdminUser[]
  ): AdminPreviewError[] {
    const errors = [] as AdminPreviewError[];
    _.forEach(users, (user: AdminUser) => {
      const valdationError = this.validate(org, fac, user);
      if (valdationError && valdationError.length > 0) {
        _.forEach(valdationError, valError => {
          const error = new AdminPreviewError();
          error.Username = user.Username;
          error.Error = valError;
          errors.push(error);
        });
      }
    });
    return errors;
  }

  validate(org: Organization, fac: AdminFacility, user: AdminUser): string[] {
    const errors = [];
    const currentUser = this.session.getUser();

    // user details
    if (!user.Username) {
      errors.push('Username is required');
    }
    if (!this.isEmail(user.Username)) {
      errors.push('Username must be a valid email address');
    }
    if (!user.FirstName) {
      errors.push('First Name is required');
    }
    if (!user.LastName) {
      errors.push('Last Name is required');
    }
    if (user.IsMFAEnabled && !user.MFAPhone) {
      errors.push('MFA Phone must be present if MFA is enabled');
    }
    if (user.OfficePhone && !this.isPhone(user.OfficePhone)) {
      errors.push(
        'Office phone is not a phone number, please use a phone format'
      );
    }
    if (user.MobilePhone && !this.isPhone(user.MobilePhone)) {
      errors.push(
        'Mobile phone is not a phone number, please use a phone format'
      );
    }
    if (user.MFAPhone && !this.isPhone(user.MFAPhone)) {
      errors.push('MFA phone is not a phone number, please use a phone format');
    }
    if (user.NPI && !this.isNumber(user.NPI)) {
      errors.push('NPI is not numeric, please use digits only');
    }
    if (user.DEA && !/^([A-z]{2}\d{7})?$/.test(user.DEA)) {
      errors.push('DEA Number is not in the correct format, please check');
    }
    if (user.PractitionerId && !this.isNumber(user.PractitionerId)) {
      errors.push('Your practitioner State License Number could not be saved. Please check that you entered your license number using numerical values, without alpha characters. License numbers can range from 1-8 numerical values and should match the license number you registered with CURES.');
    }
    else if (user.PractitionerId && user.PractitionerId.length > 8) {
      errors.push('Your practitioner State License Number could not be saved. Please check that you entered your license number using numerical values, without alpha characters. License numbers can range from 1-8 numerical values and should match the license number you registered with CURES.');
    }
    // facilities
    if (org.OrganizationId !== 'MX') {
      if (user.Facilities.length < 1) {
        errors.push('User must have at least one facility selected');
      }
    }

    // validate new users only
    if (user.IsNew) {
      // org admin check
      if (currentUser.isOrgAdmin) {
        if (user.isMxAdmin()) {
          errors.push(
            'Organization administrators can not create MX administrators'
          );
        } else if (user.isOrgAdmin()) {
          errors.push(
            'Organization administrators can not create other organization administrators'
          );
        }
      } else if (currentUser.isFacAdmin) {
        if (user.isAdmin()) {
          errors.push(
            'Facility administrators can not create other administrators'
          );
        }
      }
    }

    return errors;
  }

  isEmail(value: string): boolean {
    const pattern = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return value.search(pattern) === 0;
  }

  isNumber(value: string) {
    const pattern = /^\d+$/;
    return value.search(pattern) === 0;
  }

  isPhone(value: string) {
    // this.log.trace('UserValidation.isPhone', value);
    try {
      // Google phone lib accepts slashes instead of hyphens, but AWS will reject them so we should too
      const pattern = /\//;
      if (pattern.test(value)) {
        return false;
      }
      const number = phoneUtil.parseAndKeepRawInput(value, 'US');
      return phoneUtil.isPossibleNumber(number);
    } catch (x) {
      // If this fails, it may be due to a non-numeric phone
      return false;
    }
  }
}
